"use client";

import Link from "next/link";

const ACTION_CLASS =
  "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors";

const LAYOUT = {
  fullscreen: { container: "min-h-screen", heading: "text-2xl" },
  nested: { container: "flex-1", heading: "text-xl" },
} as const;

type Layout = keyof typeof LAYOUT;

// ---------------------------------------------------------------------------
// ErrorContent
// ---------------------------------------------------------------------------

interface ErrorContentProps {
  layout: Layout;
  heading: string;
  description: string;
  error: Error & { digest?: string };
  reset: () => void;
}

export function ErrorContent({
  layout,
  heading,
  description,
  error,
  reset,
}: ErrorContentProps) {
  const { container, heading: headingSize } = LAYOUT[layout];
  return (
    <div className={`flex ${container} items-center justify-center p-6`}>
      <div className="max-w-md text-center space-y-4">
        <h1 className={`${headingSize} font-semibold`}>{heading}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
        {error.digest && (
          <p className="text-xs text-muted-foreground">
            Error ID: {error.digest}
          </p>
        )}
        <button onClick={reset} className={ACTION_CLASS}>
          Try again
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotFoundContent
// ---------------------------------------------------------------------------

interface NotFoundContentProps {
  layout: Layout;
  heading: string;
  description: string;
  linkHref: string;
  linkText: string;
}

export function NotFoundContent({
  layout,
  heading,
  description,
  linkHref,
  linkText,
}: NotFoundContentProps) {
  const { container, heading: headingSize } = LAYOUT[layout];
  return (
    <div className={`flex ${container} items-center justify-center p-6`}>
      <div className="max-w-md text-center space-y-4">
        <h1 className={`${headingSize} font-semibold`}>{heading}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
        <Link href={linkHref} className={`inline-block ${ACTION_CLASS}`}>
          {linkText}
        </Link>
      </div>
    </div>
  );
}
